import './cutscene.css';

import { CharacterRoster, EMPTY_ROSTER, initialOf } from '../story/characters';
import {
  cutsceneCast,
  moodOf,
  type Cutscene,
  type CutsceneCastMember,
  type CutsceneScene,
} from '../story/cutscene';
import { backdropUrl, loadCharacters, loadCutscene, portraitUrl } from '../story/storyLoader';

const CHAR_INTERVAL_MS = 26;
const SCENE_ENTER_MS = 300;
const LINE_ENTER_MS = 200;

export type CutsceneOutcome = 'finished' | 'skipped';

export interface CutsceneScreenOptions {
  readonly container?: HTMLElement;
  readonly roster?: CharacterRoster | null;
  readonly onEnd?: (outcome: CutsceneOutcome) => void;
}

export class CutsceneScreen {
  private readonly cutscene: Cutscene;
  private readonly cast: readonly CutsceneCastMember[];
  private readonly roster: CharacterRoster;
  private readonly container: HTMLElement;
  private readonly onEnd: ((outcome: CutsceneOutcome) => void) | undefined;
  private readonly motionQuery: MediaQueryList | null;

  private readonly root = document.createElement('div');
  private readonly wash = document.createElement('div');
  private readonly dots = document.createElement('ol');
  private readonly counter = document.createElement('p');
  private readonly skipButton = document.createElement('button');
  private readonly stage = document.createElement('div');
  private readonly box = document.createElement('div');
  private readonly speakerLabel = document.createElement('span');
  private readonly moodLabel = document.createElement('span');
  private readonly lineGhost = document.createElement('span');
  private readonly lineTyped = document.createElement('span');
  private readonly beats = document.createElement('span');
  private readonly continueButton = document.createElement('button');
  private readonly announcer = document.createElement('p');
  private readonly portraits = new Map<string, HTMLElement>();

  private confirmPanel: HTMLElement | null = null;
  private previouslyFocused: HTMLElement | null = null;
  private sceneIndex = 0;
  private lineIndex = 0;
  private revealed = 0;
  private frame: number | null = null;
  private mounted = false;

  constructor(cutscene: Cutscene, options: CutsceneScreenOptions = {}) {
    this.cutscene = cutscene;
    this.cast = cutsceneCast(cutscene);
    this.roster = options.roster ?? EMPTY_ROSTER;
    this.container = options.container ?? document.body;
    this.onEnd = options.onEnd;
    this.motionQuery =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;
    this.buildDom();
  }

  mount(): void {
    if (this.mounted) return;
    this.mounted = true;

    this.previouslyFocused = document.activeElement as HTMLElement | null;
    document.body.classList.add('cutscene-open');
    this.container.append(this.root);

    window.addEventListener('keydown', this.handleKeydown, true);
    this.root.addEventListener('click', this.handleClick);

    this.renderScene({ sceneChanged: true });
    this.continueButton.focus();
  }

  unmount(): void {
    if (!this.mounted) return;
    this.mounted = false;

    this.stopTypewriter();
    window.removeEventListener('keydown', this.handleKeydown, true);
    this.root.removeEventListener('click', this.handleClick);
    this.root.remove();
    document.body.classList.remove('cutscene-open');
    this.previouslyFocused?.focus?.();
  }

  private buildDom(): void {
    this.root.className = 'cutscene';
    this.root.tabIndex = -1;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', 'Story scene');

    this.wash.className = 'cutscene__wash';
    this.wash.setAttribute('aria-hidden', 'true');

    const frame = document.createElement('div');
    frame.className = 'cutscene__frame';
    frame.setAttribute('aria-hidden', 'true');

    this.root.append(
      this.buildBackdrop(),
      this.wash,
      frame,
      this.buildBar(),
      this.buildStage(),
      this.buildBox(),
    );

    this.announcer.className = 'cutscene__sr';
    this.announcer.setAttribute('role', 'status');
    this.announcer.setAttribute('aria-live', 'polite');
    this.root.append(this.announcer);
  }

  private buildBackdrop(): HTMLElement {
    const backdrop = document.createElement('div');
    backdrop.className = 'cutscene__backdrop';
    backdrop.setAttribute('aria-hidden', 'true');

    const image = document.createElement('img');
    image.src = backdropUrl(this.cutscene.id);
    image.alt = '';
    image.decoding = 'async';
    image.addEventListener('load', () => backdrop.classList.add('is-loaded'));
    image.addEventListener('error', () => backdrop.remove());
    backdrop.append(image);
    return backdrop;
  }

  private buildBar(): HTMLElement {
    const bar = document.createElement('header');
    bar.className = 'cutscene__bar';

    this.dots.className = 'cutscene__dots';
    this.dots.setAttribute('aria-hidden', 'true');
    for (let index = 0; index < this.cutscene.scenes.length; index++) {
      this.dots.append(document.createElement('li'));
    }

    this.counter.className = 'cutscene__count';

    const progress = document.createElement('div');
    progress.className = 'cutscene__progress';
    progress.append(this.dots, this.counter);

    this.skipButton.type = 'button';
    this.skipButton.className = 'cutscene__btn cutscene__skip';
    this.skipButton.textContent = 'Skip';
    this.skipButton.setAttribute('aria-label', 'Skip this cutscene');
    this.skipButton.addEventListener('click', () => this.openSkipConfirm());

    bar.append(progress, this.skipButton);
    return bar;
  }

  private buildStage(): HTMLElement {
    this.stage.className = 'cutscene__stage';
    this.stage.setAttribute('aria-hidden', 'true');
    if (this.cast.length === 1) this.stage.classList.add('cutscene__stage--solo');

    for (const member of this.cast) {
      const figure = this.buildCastMember(member);
      this.portraits.set(member.speaker, figure);
      this.stage.append(figure);
    }
    return this.stage;
  }

  private buildCastMember(member: CutsceneCastMember): HTMLElement {
    const name = this.roster.nameOf(member.speaker, shortName(member.displayName));
    const accent = this.roster.accentOf(member.speaker);

    const figure = document.createElement('figure');
    figure.className = 'cutscene__cast-member';
    figure.style.setProperty('--speaker-accent', accent);

    const portrait = document.createElement('div');
    portrait.className = 'cutscene__portrait';

    const image = document.createElement('img');
    image.src = portraitUrl(member.speaker);
    image.alt = '';
    image.decoding = 'async';
    image.addEventListener('error', () => portrait.replaceChildren(buildBadge(name)));
    portrait.append(image);

    const caption = document.createElement('figcaption');
    caption.className = 'cutscene__cast-name';
    caption.textContent = name;

    figure.append(portrait, caption);
    return figure;
  }

  private buildBox(): HTMLElement {
    this.box.className = 'cutscene__box';

    this.speakerLabel.className = 'cutscene__speaker';
    this.moodLabel.className = 'cutscene__mood';

    const nameplate = document.createElement('div');
    nameplate.className = 'cutscene__nameplate';
    nameplate.append(this.speakerLabel, this.moodLabel);

    this.lineGhost.className = 'cutscene__line-ghost';
    this.lineTyped.className = 'cutscene__line-typed';

    const line = document.createElement('p');
    line.className = 'cutscene__line';
    line.setAttribute('aria-hidden', 'true');
    line.append(this.lineGhost, this.lineTyped);

    this.beats.className = 'cutscene__beats';
    this.beats.setAttribute('aria-hidden', 'true');

    this.continueButton.type = 'button';
    this.continueButton.className = 'cutscene__btn cutscene__continue';
    this.continueButton.addEventListener('click', () => this.advance());

    const foot = document.createElement('div');
    foot.className = 'cutscene__foot';
    foot.append(this.beats, this.continueButton);

    this.box.append(nameplate, line, foot);
    return this.box;
  }

  private renderScene(options: { sceneChanged: boolean }): void {
    const scene = this.currentScene;
    if (!scene) {
      this.finish('finished');
      return;
    }

    const mood = moodOf(scene);
    this.root.dataset['mood'] = mood;
    this.moodLabel.textContent = mood;
    this.speakerLabel.textContent = scene.speakerDisplayName;
    this.box.style.setProperty('--speaker-accent', this.roster.accentOf(scene.speaker));

    this.renderCast(scene);
    this.renderProgress(scene);
    this.renderBeats(scene);

    const line = this.currentLine;
    this.lineGhost.textContent = line;
    this.announcer.textContent = `${scene.speakerDisplayName}: ${line}`;

    if (!this.reducedMotion) {
      const duration = options.sceneChanged ? SCENE_ENTER_MS : LINE_ENTER_MS;
      this.box.animate(
        [
          { opacity: 0, transform: 'translateY(8px)' },
          { opacity: 1, transform: 'none' },
        ],
        { duration, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
      );
    }

    this.startTypewriter();
  }

  private renderCast(scene: CutsceneScene): void {
    for (const [speaker, figure] of this.portraits) {
      figure.classList.toggle('is-active', speaker === scene.speaker);
    }
  }

  private renderProgress(scene: CutsceneScene): void {
    const total = this.cutscene.scenes.length;
    this.counter.textContent = `Scene ${this.sceneIndex + 1} of ${total}`;
    this.counter.setAttribute(
      'aria-label',
      `Scene ${this.sceneIndex + 1} of ${total}, ${scene.speakerDisplayName} speaking`,
    );

    Array.from(this.dots.children).forEach((dot, index) => {
      dot.classList.toggle('is-active', index === this.sceneIndex);
      dot.classList.toggle('is-past', index < this.sceneIndex);
    });
  }

  private renderBeats(scene: CutsceneScene): void {
    this.beats.replaceChildren();
    if (scene.lines.length < 2) return;

    for (let index = 0; index < scene.lines.length; index++) {
      const beat = document.createElement('i');
      if (index === this.lineIndex) beat.className = 'is-active';
      else if (index < this.lineIndex) beat.className = 'is-past';
      this.beats.append(beat);
    }
  }

  private paintLine(): void {
    const line = this.currentLine;
    this.lineTyped.textContent = line.slice(0, this.revealed);
    const complete = this.revealed >= line.length;
    this.box.classList.toggle('is-typing', !complete);
    this.continueButton.textContent = complete ? this.continueLabel() : 'Reveal line';
    this.continueButton.setAttribute(
      'aria-label',
      complete ? this.continueLabel() : 'Reveal the full line',
    );
  }

  private continueLabel(): string {
    return this.isLastLineOfCutscene ? 'Close' : 'Next';
  }

  private startTypewriter(): void {
    this.stopTypewriter();
    const line = this.currentLine;

    if (this.reducedMotion) {
      this.revealed = line.length;
      this.paintLine();
      return;
    }

    this.revealed = 0;
    this.paintLine();

    const started = performance.now();
    const step = (now: number): void => {
      const target = Math.min(line.length, Math.floor((now - started) / CHAR_INTERVAL_MS));
      if (target !== this.revealed) {
        this.revealed = target;
        this.paintLine();
      }
      this.frame = this.revealed < line.length ? requestAnimationFrame(step) : null;
    };
    this.frame = requestAnimationFrame(step);
  }

  private stopTypewriter(): void {
    if (this.frame === null) return;
    cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  private advance(): void {
    const scene = this.currentScene;
    if (!scene) return;

    if (this.revealed < this.currentLine.length) {
      this.stopTypewriter();
      this.revealed = this.currentLine.length;
      this.paintLine();
      return;
    }

    if (this.lineIndex < scene.lines.length - 1) {
      this.lineIndex++;
      this.renderScene({ sceneChanged: false });
      return;
    }

    if (this.sceneIndex < this.cutscene.scenes.length - 1) {
      this.sceneIndex++;
      this.lineIndex = 0;
      this.renderScene({ sceneChanged: true });
      return;
    }

    this.finish('finished');
  }

  private finish(outcome: CutsceneOutcome): void {
    this.unmount();
    this.onEnd?.(outcome);
  }

  private readonly handleClick = (event: MouseEvent): void => {
    const target = event.target;
    if (target instanceof Element && target.closest('button')) return;
    if (this.confirmPanel) return;
    this.advance();
  };

  private readonly handleKeydown = (event: KeyboardEvent): void => {
    if (!this.root.isConnected) return;

    if (event.key === 'Tab') {
      this.trapFocus(event);
      return;
    }

    if (this.confirmPanel) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeSkipConfirm();
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.openSkipConfirm();
      return;
    }

    const onButton = event.target instanceof Element && event.target.closest('button');
    if ((event.key === ' ' || event.key === 'Enter') && onButton) return;

    if (event.key === ' ' || event.key === 'Enter' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.advance();
    }
  };

  private trapFocus(event: KeyboardEvent): void {
    const focusable = this.focusables();
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;

    const active = document.activeElement;
    if (event.shiftKey && (active === first || !this.root.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !this.root.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  private focusables(): HTMLElement[] {
    const scope = this.confirmPanel ?? this.root;
    return Array.from(scope.querySelectorAll<HTMLElement>('button:not([disabled])'));
  }

  private openSkipConfirm(): void {
    if (this.confirmPanel) return;
    this.stopTypewriter();

    const panel = document.createElement('div');
    panel.className = 'cutscene__confirm';
    panel.setAttribute('role', 'alertdialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', 'cutscene-confirm-title');

    const title = document.createElement('h2');
    title.id = 'cutscene-confirm-title';
    title.textContent = 'Skip this cutscene?';

    const body = document.createElement('p');
    body.textContent = 'The story continues without it.';

    const keep = document.createElement('button');
    keep.type = 'button';
    keep.className = 'cutscene__btn';
    keep.textContent = 'Keep watching';
    keep.addEventListener('click', () => this.closeSkipConfirm());

    const skip = document.createElement('button');
    skip.type = 'button';
    skip.className = 'cutscene__btn cutscene__btn--strong';
    skip.textContent = 'Skip';
    skip.addEventListener('click', () => this.finish('skipped'));

    const actions = document.createElement('div');
    actions.className = 'cutscene__confirm-actions';
    actions.append(keep, skip);

    panel.append(title, body, actions);
    this.confirmPanel = panel;
    this.root.append(panel);
    keep.focus();
  }

  private closeSkipConfirm(): void {
    if (!this.confirmPanel) return;
    this.confirmPanel.remove();
    this.confirmPanel = null;
    this.continueButton.focus();
    this.startTypewriter();
  }

  private get currentScene(): CutsceneScene | undefined {
    return this.cutscene.scenes[this.sceneIndex];
  }

  private get currentLine(): string {
    return this.currentScene?.lines[this.lineIndex] ?? '';
  }

  private get isLastLineOfCutscene(): boolean {
    const scene = this.currentScene;
    if (!scene) return true;
    return (
      this.sceneIndex === this.cutscene.scenes.length - 1 &&
      this.lineIndex === scene.lines.length - 1
    );
  }

  private get reducedMotion(): boolean {
    return this.motionQuery?.matches ?? false;
  }
}

export function playCutsceneData(
  cutscene: Cutscene,
  options: CutsceneScreenOptions = {},
): Promise<CutsceneOutcome> {
  return new Promise((resolve) => {
    const screen = new CutsceneScreen(cutscene, {
      ...options,
      onEnd: (outcome) => {
        options.onEnd?.(outcome);
        resolve(outcome);
      },
    });
    screen.mount();
  });
}

export async function playCutscene(
  cutsceneId: string,
  options: CutsceneScreenOptions = {},
): Promise<CutsceneOutcome> {
  const [cutscene, roster] = await Promise.all([
    loadCutscene(cutsceneId),
    loadCharacters().catch(() => EMPTY_ROSTER),
  ]);
  return playCutsceneData(cutscene, { roster, ...options });
}

function buildBadge(name: string): HTMLElement {
  const badge = document.createElement('span');
  badge.className = 'cutscene__badge';
  badge.textContent = initialOf(name);
  return badge;
}

function shortName(displayName: string): string {
  return displayName.replace(/\s*\(.*\)\s*$/, '').trim() || displayName;
}
